import { formatAddress, formatCreatedDate } from '@core/logic';
import { useAppDispatch } from '@core/redux/hooks';
import {
  useGetPointElementOrderNumbersQuery,
  useGetSplineElementOrderNumbersQuery,
} from '@core/redux/query';
import { PencilOutline, TrashcanOutline } from '@deepup/icons';
import { elementActions, elementSelectors } from '@elements/redux';
import { pointElementSelectors, pointElementThunks } from '@elements/redux/point-element.slice';
import { COMMENT_PREFIX, PointElement, SplineElement } from '@elements/types';
import { IconButton, ListItemButton, ListItemText, Stack } from '@mui/material';
import { setEditMode } from '@projects/edit-modes';
import { EditMode } from '@projects/edit-modes/types';
import { trenchSelectors, trenchThunks } from '@trenches/redux';
import { viewer3dActions, viewer3dSelectors } from '@viewer3D/redux';
import { useSelector } from 'react-redux';
import { useIntersectionObserver } from '@core/useIntersectionObserver';
import { useMemo, useEffect, useRef, useState } from 'react';
import {
  ElementConfirmDeleteDialog,
  PointElementDialog,
  SplineElementDialog,
} from '../ElementDialog';

interface ElementListItemData {
  title: string;
  subtitle: string;
  element: SplineElement | PointElement;
  isSelected: boolean;
  selectElement: () => void;
  deleteElement?: () => void;
  isLabeled: boolean;
}

export const useSplineElementConnect = (
  splineElementId: string,
  skip?: boolean,
): ElementListItemData => {
  const dispatch = useAppDispatch();
  const splineElement = useSelector(elementSelectors.selectById(splineElementId))!;
  const trenchId = useSelector(trenchSelectors.findByElementId(splineElementId))?.id;
  const [selectedElementId] = useSelector(viewer3dSelectors.selectedElementIds);
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);
  const isLabeled = !!trenchId;
  const { data: splineElementOrderNumbers } = useGetSplineElementOrderNumbersQuery(
    splineElementId,
    skip,
  );

  const hasOrderNumbers = splineElementOrderNumbers && splineElementOrderNumbers.length > 0;

  const checkboxInfo = splineElement.comment
    ?.split('.')
    .map((line) => {
      if (line.startsWith(COMMENT_PREFIX)) {
        return `${line.replaceAll(COMMENT_PREFIX, '')}`;
      }

      return `${line}`;
    })
    .join('\n');

  return {
    title: splineElement.name,
    subtitle:
      `${formatCreatedDate(splineElement.createdAt)} ${!isLabeled ? ' (not labeled!)' : ''}` +
      (hasOrderNumbers ? `\n${splineElementOrderNumbers.map((on) => on.value).join(', ')}` : '') +
      (checkboxInfo ? `\n${checkboxInfo}` : ''),
    element: { ...splineElement, orderNumbers: splineElementOrderNumbers },
    isSelected: selectedElementId === splineElementId,
    isLabeled,
    selectElement: () => {
      if (selectedElementId === splineElementId) return;
      setEditMode(EditMode.Element);
      dispatch(viewer3dActions.setSelectedPointId());
      if (trenchId) {
        // also select the related trench
        dispatch(viewer3dActions.setSelectedTrenchId(trenchId));
      }
      dispatch(viewer3dActions.setSelectedElementIds([splineElementId]));
    },
    deleteElement: async () => {
      const { payload } = await dispatch(
        trenchThunks.deleteElement({
          path: ['projects', projectId!, 'splineElements', splineElementId],
        }),
      );
      if (payload instanceof Error) return;
      dispatch(elementActions.removeOne(splineElementId));
      dispatch(viewer3dActions.removeElementIdFromSelection(splineElementId));
    },
  };
};

export const usePointElementConnect = (
  pointElementId: string,
  skip?: boolean,
): ElementListItemData => {
  const dispatch = useAppDispatch();
  const pointElement = useSelector(pointElementSelectors.selectById(pointElementId))!;
  const projectId = useSelector(viewer3dSelectors.selectedProjectId)!;
  const hasPointsChildren = useSelector(pointElementSelectors.selectAll).some(
    (point) => point.parentPointElementId === pointElementId,
  );
  const hasSplinesChildren = useSelector(elementSelectors.selectAll).some(
    (element) => element.parentPointElementId === pointElementId,
  );
  const hasChildren = hasPointsChildren || hasSplinesChildren;
  const [selectedElementId] = useSelector(viewer3dSelectors.selectedElementIds);
  const isLabeled = pointElement.pointId !== undefined;
  const { data: pointElementOrderNumbers } = useGetPointElementOrderNumbersQuery(
    pointElementId,
    skip,
  );

  const hasOrderNumbers = pointElementOrderNumbers && pointElementOrderNumbers.length > 0;

  return {
    title: formatAddress(pointElement.address),
    isLabeled,
    subtitle:
      (pointElement.name ? pointElement.name + ', ' : '') +
      formatCreatedDate(pointElement.createdAt) +
      ' ' +
      (!isLabeled ? ' (not labeled!)' : '') +
      (hasOrderNumbers ? `\n${pointElementOrderNumbers.map((on) => on.value).join(', ')}` : ''),
    element: { ...pointElement, orderNumbers: pointElementOrderNumbers },
    isSelected: selectedElementId === pointElementId,
    selectElement: () => {
      if (selectedElementId === pointElementId) return;
      setEditMode(EditMode.Element);
      dispatch(viewer3dActions.setSelectedElementIds([pointElementId]));
      pointElement.pointId && dispatch(viewer3dActions.setSelectedPointId(pointElement.pointId));
    },
    deleteElement: hasChildren
      ? undefined
      : async () => {
          const { payload } = await dispatch(
            pointElementThunks.delete({
              path: ['projects', projectId, 'pointElements', pointElementId],
              id: pointElementId,
            }),
          );
          if (payload instanceof Error) return;
          dispatch(viewer3dActions.setSelectedElementIds([]));
        },
  };
};

interface ElementListItemInnerProps {
  isPointElement: boolean;
  itemData: ElementListItemData;
  innerRef: React.RefObject<HTMLDivElement | null>;
}

const ElementListItemInner = ({
  isPointElement,
  itemData,
  innerRef,
}: ElementListItemInnerProps) => {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [addUpdateDialogOpen, setAddUpdateDialogOpen] = useState(false);

  const { title, subtitle, element, isSelected, selectElement, deleteElement, isLabeled } =
    itemData;

  const handleDelete = async () => {
    setConfirmDeleteOpen(false);
    deleteElement?.();
  };

  useEffect(() => {
    if (!isSelected || !innerRef.current) return;
    const timer = setTimeout(() => {
      innerRef.current?.scrollIntoView({ block: 'center', behavior: 'auto' });
    }, 500);
    return () => clearTimeout(timer);
  }, [isSelected, innerRef]);

  return (
    <>
      <ListItemButton
        ref={innerRef}
        dense
        aria-label="element-list-item"
        selected={isSelected}
        title={!isLabeled ? 'Element is not labeled on any trench' : undefined}
        autoFocus={isSelected}
        onClick={selectElement}
        sx={{
          backgroundColor: isLabeled
            ? undefined
            : (theme) => theme.palette.error.dark + ' !important',
        }}
      >
        <ListItemText
          primary={title}
          secondary={subtitle}
          primaryTypographyProps={{ title, noWrap: true }}
          secondaryTypographyProps={{ title: subtitle, noWrap: true, whiteSpace: 'pre-line' }}
        />
        <Stack direction="row" sx={{ display: 'none', '*:hover > &': { display: 'inline-flex' } }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setAddUpdateDialogOpen(true);
            }}
            aria-label="edit"
            title="Open edit dialog for Element"
          >
            <PencilOutline />
          </IconButton>
          {deleteElement && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmDeleteOpen(true);
              }}
              aria-label="delete"
              title="Delete Element"
            >
              <TrashcanOutline />
            </IconButton>
          )}
        </Stack>
      </ListItemButton>
      {confirmDeleteOpen && (
        <ElementConfirmDeleteDialog
          onClose={() => setConfirmDeleteOpen(false)}
          onConfirm={handleDelete}
        />
      )}
      {addUpdateDialogOpen &&
        (isPointElement ? (
          <PointElementDialog
            elementToUpdate={element as PointElement}
            onClose={() => setAddUpdateDialogOpen(false)}
          />
        ) : (
          <SplineElementDialog
            elementToUpdate={element as SplineElement}
            onClose={() => setAddUpdateDialogOpen(false)}
            onDelete={() => {
              deleteElement?.();
              setAddUpdateDialogOpen(false);
            }}
          />
        ))}
    </>
  );
};

const PointElementListItem = ({ elementId }: { elementId: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [skip, setSkip] = useState(true);
  const intersections = useIntersectionObserver(useMemo(() => [ref], [ref]));
  const isIntersecting = intersections[0]?.isIntersecting;

  useEffect(() => {
    let timer: number | undefined;
    if (isIntersecting) {
      timer = window.setTimeout(() => setSkip(false), 100);
    } else {
      setSkip(true);
    }
    return () => window.clearTimeout(timer);
  }, [isIntersecting]);

  const itemData = usePointElementConnect(elementId, skip);

  return <ElementListItemInner isPointElement={true} itemData={itemData} innerRef={ref} />;
};

const SplineElementListItem = ({ elementId }: { elementId: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [skip, setSkip] = useState(true);
  const intersections = useIntersectionObserver(useMemo(() => [ref], [ref]));
  const isIntersecting = intersections[0]?.isIntersecting;

  useEffect(() => {
    let timer: number | undefined;
    if (isIntersecting) {
      timer = window.setTimeout(() => setSkip(false), 100);
    } else {
      setSkip(true);
    }
    return () => window.clearTimeout(timer);
  }, [isIntersecting]);

  const itemData = useSplineElementConnect(elementId, skip);

  return <ElementListItemInner isPointElement={false} itemData={itemData} innerRef={ref} />;
};

export const ElementListItem = ({ elementId }: { elementId: string }) => {
  const isPointElement = !!useSelector(pointElementSelectors.selectById(elementId));

  if (isPointElement) {
    return <PointElementListItem elementId={elementId} />;
  }
  return <SplineElementListItem elementId={elementId} />;
};
